// src/components/games/diceMatch/DiceMatchMenu.tsx

import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonBadge,
  IonBackButton,
  IonButtons,
  IonGrid,
  IonRow,
  IonCol,
  IonProgressBar,
  useIonViewWillEnter,
} from '@ionic/react';
import { play, trophy, statsChart, star, arrowUp, gift } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import {
  getMetaProgression,
  getPersonalBest,
  getLeaderboard,
  MetaProgression,
  DiceMatchScore,
} from '../../../services/diceMatchService';
import './DiceMatchMenu.css';

const DiceMatchMenu: React.FC = () => {
  const history = useHistory();
  const [meta, setMeta] = useState<MetaProgression | null>(null);
  const [personalBest, setPersonalBest] = useState<number>(0);
  const [leaderboard, setLeaderboard] = useState<DiceMatchScore[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  // Refresh data when returning to this page
  useIonViewWillEnter(() => {
    loadData();
  });

  const loadData = async () => {
    setLoading(true);
    const [metaData, bestScore, leaders] = await Promise.all([
      getMetaProgression(),
      getPersonalBest(),
      getLeaderboard(10),
    ]);
    setMeta(metaData);
    setPersonalBest(bestScore);
    setLeaderboard(leaders);
    setLoading(false);
  };

  const handlePlayNow = () => {
    history.push('/games/dice-match');
  };

  const handleUpgrades = () => {
    history.push('/games/dice-match/upgrades');
  };

  const handleTrinkets = () => {
    history.push('/games/dice-match/trinkets');
  };

  if (loading || !meta) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/games" />
            </IonButtons>
            <IonTitle>Dice Match</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <IonProgressBar type="indeterminate" />
            <p>Loading...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const totalUpgrades =
    meta.upgrade_starting_dice +
    meta.upgrade_starting_grid +
    meta.upgrade_currency_multiplier;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/games" />
          </IonButtons>
          <IonTitle>Dice Match</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="dice-match-menu-content">
        <div className="dice-match-menu-container">
          {/* Hero Card */}
          <IonCard className="hero-card">
            <IonCardContent>
              <div className="hero-content">
                <div className="hero-icon">🎲</div>
                <h1>Dice Match</h1>
                <p>Merge dice to reach the ultimate sparkle!</p>
                <IonButton
                  expand="block"
                  size="large"
                  color="primary"
                  onClick={handlePlayNow}
                  style={{ marginTop: '16px' }}
                >
                  <IonIcon icon={play} slot="start" />
                  Play Now
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Leaderboard Card */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={trophy} /> Top Players
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {leaderboard.length > 0 ? (
                <div className="leaderboard-list">
                  {leaderboard.map((entry, index) => (
                    <div key={entry.id} className="leaderboard-entry">
                      <div className="rank-badge">
                        {index === 0 && '🥇'}
                        {index === 1 && '🥈'}
                        {index === 2 && '🥉'}
                        {index > 2 && `#${index + 1}`}
                      </div>
                      <div className="player-info">
                        <div className="player-name">
                          {entry.display_name || entry.username || 'Anonymous'}
                        </div>
                        <div className="player-stats">
                          <span className="score">{entry.score.toLocaleString()} pts</span>
                          <span className="highest-die">{entry.highest_die}</span>
                          <span className="sparkles">✨ {entry.sparkles_created}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: 'var(--ion-color-medium)' }}>
                  No scores yet. Be the first to set a record!
                </p>
              )}
            </IonCardContent>
          </IonCard>

          {/* Stats Card */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={statsChart} /> Your Stats
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonGrid>
                <IonRow>
                  <IonCol size="6">
                    <div className="stat-box">
                      <div className="stat-label">Personal Best</div>
                      <div className="stat-value">{personalBest.toLocaleString()}</div>
                    </div>
                  </IonCol>
                  <IonCol size="6">
                    <div className="stat-box">
                      <div className="stat-label">Games Played</div>
                      <div className="stat-value">{meta.total_games_played}</div>
                    </div>
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol size="6">
                    <div className="stat-box">
                      <div className="stat-label">Total Sparkles</div>
                      <div className="stat-value">✨ {meta.total_sparkles_created}</div>
                    </div>
                  </IonCol>
                  <IonCol size="6">
                    <div className="stat-box">
                      <div className="stat-label">Lifetime Score</div>
                      <div className="stat-value">{meta.total_score.toLocaleString()}</div>
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </IonCard>

          {/* Meta Currency Card */}
          <IonCard className="currency-card">
            <IonCardContent>
              <div className="currency-display">
                <div className="currency-icon">⭐</div>
                <div className="currency-info">
                  <div className="currency-label">Meta Currency</div>
                  <div className="currency-amount">{meta.meta_currency}</div>
                  <div className="currency-subtitle">
                    Use to purchase permanent upgrades
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Upgrades Card */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={arrowUp} /> Permanent Upgrades
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="upgrades-preview">
                <div className="upgrade-item">
                  <span className="upgrade-name">🎲 Starting Dice</span>
                  <IonBadge color="primary">Level {meta.upgrade_starting_dice}</IonBadge>
                </div>
                <div className="upgrade-item">
                  <span className="upgrade-name">📐 Grid Size</span>
                  <IonBadge color="secondary">Level {meta.upgrade_starting_grid}</IonBadge>
                </div>
                <div className="upgrade-item">
                  <span className="upgrade-name">💰 Currency Bonus</span>
                  <IonBadge color="success">Level {meta.upgrade_currency_multiplier}</IonBadge>
                </div>
              </div>

              <IonButton
                expand="block"
                fill="outline"
                onClick={handleUpgrades}
                style={{ marginTop: '16px' }}
              >
                <IonIcon icon={star} slot="start" />
                View All Upgrades ({totalUpgrades} purchased)
              </IonButton>
            </IonCardContent>
          </IonCard>

          {/* Trinkets Card */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={gift} /> Trinkets
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <p>
                Collect powerful trinkets to enhance your gameplay! Trinkets provide
                temporary boosts, permanent bonuses, or special one-time effects.
              </p>
              <IonButton
                expand="block"
                fill="outline"
                onClick={handleTrinkets}
                style={{ marginTop: '16px' }}
              >
                <IonIcon icon={gift} slot="start" />
                Manage Trinkets
              </IonButton>
            </IonCardContent>
          </IonCard>

          {/* How to Earn Currency */}
          <IonCard className="info-card">
            <IonCardContent>
              <h4>💡 How to Earn Meta Currency</h4>
              <ul>
                <li>Meta currency = sparkles created! ✨</li>
                <li>Each sparkle you create earns 1 meta currency</li>
                <li>Focus on merging dice to reach sparkle tier</li>
                <li>Use meta currency to unlock permanent upgrades</li>
              </ul>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default DiceMatchMenu;
